import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useMemo, useState } from 'react';
import Tag from '../../../../components/Display/Tag';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import LanguageManagerGuard from '../../../../components/Language/LanguageManagerGuard';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { privateFetch } from '../../../../utils/apiHelper';
import { CONSTANT } from '../../../../utils/constants';
import { validateURL } from '../../../../utils/stringHelper';
import ImageForm from '../../common/image/ImageForm';

function ResourcePanel({ isOpen, onClose, onCreate, resource, onUpdate, loading }) {
  const [type, setType] = useState('');
  const [status, setStatus] = useState(undefined);
  const [title, setTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [buyUrl, setBuyUrl] = useState('');
  const [deezerId, setDeezerId] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(undefined);
  const [program, setProgram] = useState(undefined);
  const [programList, setProgramList] = useState([]);

  const { accountContext } = useAccountContext();

  useEffect(() => {
    if (resource) {
      setType(resource.type);
      setStatus(resource.status);
      setTitle(resource.title);
      setVideoUrl(resource.video_url);
      setBuyUrl(resource.buy_url);
      setDeezerId(resource.deezer_id);
      setDescription(resource.description);
      setImage(resource.main_image);
      setProgram(resource.program);
    } else {
      setType(CONSTANT.melismes.resourceTypes[0].value);
      setStatus(undefined);
      setTitle('');
      setVideoUrl('');
      setBuyUrl('');
      setDeezerId('');
      setDescription('');
      setImage(undefined);
      setProgram(undefined);
    }
  }, [resource, isOpen]);

  useEffect(() => {
    fetchProgramList();
  }, [isOpen]);

  const fetchProgramList = async () => {
    await privateFetch('GET', 'melismes', `/cu/${accountContext.id}/program/`).then((res) => {
      setProgramList(res);
    });
  };

  const handleValidate = () => {
    const resourcePayload = {
      type,
      status,
      title,
      video_url: videoUrl,
      deezer_id: deezerId,
      description,
      main_image: image?.id,
      program: program?.id
    };
    if (type === 'record') resourcePayload['buy_url'] = buyUrl;
    resource ? onUpdate(resourcePayload) : onCreate(resourcePayload);
  };

  const formIsValid = () => {
    return (
      title.length > 3 &&
      description.length > 3 &&
      image &&
      type &&
      validateURL(buyUrl) &&
      validateURL(videoUrl)
    );
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${resource ? 'modification' : 'création'}`}
        className="w-full"
        onClick={handleValidate}
        disabled={!formIsValid()}
        severity="success"
        loading={loading}
      />
    </div>
  );

  const dropdownTemplate = (option, props) => {
    if (option) {
      return (
        <Tag
          tag={{
            label: option.label,
            icon: option.icon,
            bgColor: option.bgColor
          }}
          rounded
        />
      );
    }
    return <span>{props.placeholder}</span>;
  };

  const programTemplate = (option, props) => {
    if (!option) return <span>{props.placeholder}</span>;
    return (
      <Tag
        tag={{
          label: option.title,
          bgColor: 'bg-slate-700',
          icon: 'program'
        }}
      />
    );
  };

  const validateYoutubeURL = useMemo(() => !videoUrl || validateURL(videoUrl), [videoUrl]);

  return (
    <Sidebar
      style={{ width: 700 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${resource ? 'Modifier' : 'Ajouter'} une ressource`}</h3>
      }>
      <LanguageManagerGuard isCreation={!resource}>
        <div className="sidebar-content-container">
          <div className="form-wrapper">
            <FormInput label={'Type de ressource'} required isValid={type}>
              <Dropdown
                id="type"
                value={type}
                onChange={(e) => setType(e.value)}
                options={CONSTANT.melismes.resourceTypes}
                optionLabel="label"
                placeholder="Veuillez sélectionner le type de ressource"
                valueTemplate={dropdownTemplate}
                itemTemplate={dropdownTemplate}
              />
            </FormInput>

            <FormInput label={'Programme lié'}>
              <Dropdown
                id="program"
                value={program}
                onChange={(e) => setProgram(e.value)}
                options={programList}
                optionLabel="title"
                placeholder="Veuillez sélectionner le programme lié"
                filter={true}
                filterInputAutoFocus={true}
                valueTemplate={programTemplate}
                itemTemplate={programTemplate}
                dataKey="id"
                showClear
              />
            </FormInput>

            {resource && (
              <FormInput key="status" label={'État de la ressource'}>
                <Dropdown
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.value)}
                  options={CONSTANT.melismes.common.statusTypes}
                  optionLabel="label"
                  placeholder="Veuillez sélectionner l'état de la ressource"
                  valueTemplate={dropdownTemplate}
                  itemTemplate={dropdownTemplate}
                />
              </FormInput>
            )}
            <FormInput label={'Titre de la ressource'} required isValid={title.length > 3}>
              <InputText
                id="title"
                value={title}
                maxLength={300}
                placeholder="Veuillez saisir le titre de la ressource"
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormInput>
            <FormInput
              label={'Url de la vidéo Youtube'}
              helpMessage={'Utiliser la fonction "Partager la vidéo" de Youtube'}
              errorMessage={
                !validateYoutubeURL &&
                "L'URL doit avoir la forme https://youtu.be/HSQq_zEeEcM?si=4qk_KCcecxC95Zq"
              }>
              <InputText
                id="website_url"
                value={videoUrl}
                maxLength={300}
                placeholder="Veuillez saisir l'url de la vidéo Youtube"
                onChange={(e) => setVideoUrl(e.target.value)}
                invalid={!validateURL(videoUrl)}
              />
            </FormInput>

            {type === 'record' && (
              <FormInput label={"URL d'achat"}>
                <InputText
                  id="buyUrl"
                  value={buyUrl}
                  placeholder="Veuillez saisir l'url d'achat"
                  onChange={(e) => setBuyUrl(e.target.value)}
                />
              </FormInput>
            )}

            <FormInput label={'ID Deezer du ressource'}>
              <InputText
                id="deezer_id"
                rows={3}
                autoResize
                value={deezerId}
                placeholder="Veuillez saisir l'ID Deezer"
                onChange={(e) => setDeezerId(e.target.value)}
              />
            </FormInput>

            <FormInput label={'Description'} required isValid={description.length > 3}>
              <RichTextEditor
                value={description}
                onChange={(value) => {
                  setDescription(value);
                }}
              />
            </FormInput>

            <ImageForm image={image} setImage={setImage} deleteImage={() => setImage(undefined)} />
          </div>
          {footerButtons}
        </div>
      </LanguageManagerGuard>
    </Sidebar>
  );
}

export default ResourcePanel;
